import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReactElement, ReactNode, useState } from 'react'
import { DeleteIcon, EditIcon, MigrateIcon } from 'src/v3/core/icons'

type CourseRef = {
  courseID: string
  courseName: string
}

type LibraryCardBaseProps = {
  name: string
  description?: string
  courses: CourseRef[]
  isMigratable: boolean
  onEdit: () => void
  onDelete: () => void
  onMigrate: () => void
  itemLabel: string
  mediaContent?: ReactNode
}

export default function LibraryCardBase({
  name,
  description,
  courses,
  isMigratable,
  onEdit,
  onDelete,
  onMigrate,
  itemLabel,
  mediaContent,
}: LibraryCardBaseProps): ReactElement {
  let canDelete = courses.length === 0
  let [confirmMigrateOpen, setConfirmMigrateOpen] = useState(false)

  return (
    <Card
      className="flex flex-col h-full transition-shadow duration-200"
      sx={{ bgcolor: 'background.paperDark' }}
    >
      {mediaContent}
      <CardContent className="flex flex-col flex-1 gap-2">
        <Typography
          variant="subtitle2"
          component="h3"
          className="font-semibold leading-snug"
        >
          {name}
        </Typography>

        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}

        <Box className="pt-2 mt-auto">
          {courses.length === 0 ? (
            <Typography variant="caption" color="text.disabled">
              Not associated with any course
            </Typography>
          ) : (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                className="font-semibold"
              >
                Associated Courses:
              </Typography>
              <Box
                component="ul"
                className="pl-2 m-0 list-disc"
                aria-label="Associated courses"
              >
                {courses.map((c) => (
                  <Typography
                    key={c.courseID}
                    component="li"
                    variant="caption"
                    color="text.secondary"
                  >
                    {c.courseName}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </Box>
      </CardContent>
      <Box
        className="flex items-center justify-between px-2 pt-2 pb-2 border-t"
        sx={{ borderColor: 'divider' }}
      >
        {isMigratable ? (
          <Typography
            variant="caption"
            color="warning.main"
            data-tour="library-item-migrate-label"
          >
            Requires migration
          </Typography>
        ) : (
          <Box />
        )}
        <Box className="flex gap-1">
          {isMigratable && courses.length > 0 && (
            <Tooltip title="Migrate">
              <IconButton
                size="small"
                color="warning"
                onClick={() => setConfirmMigrateOpen(true)}
                aria-label={`Migrate ${itemLabel}`}
              >
                <MigrateIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={
              isMigratable ? `Migrate this ${itemLabel} before editing` : 'Edit'
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={onEdit}
                disabled={isMigratable}
                aria-label={`Edit ${itemLabel}`}
              >
                <EditIcon
                  fontSize="medium"
                  color={isMigratable ? 'disabled' : 'primary'}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              canDelete
                ? 'Delete'
                : 'Remove course associations before deleting'
            }
          >
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={onDelete}
                disabled={!canDelete}
                aria-label={`Delete ${itemLabel}`}
              >
                <DeleteIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Dialog
        open={confirmMigrateOpen}
        onClose={() => setConfirmMigrateOpen(false)}
        fullWidth
        aria-labelledby="confirm-migrate-dialog-title"
        maxWidth="xs"
      >
        <DialogTitle id="confirm-migrate-dialog-title">
          Confirm Migration
        </DialogTitle>
        <DialogContent>
          <Typography>
            Migrate <strong>{name}</strong> to the latest version? This will not
            affect current functionality.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMigrateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmMigrateOpen(false)
              onMigrate()
            }}
          >
            Migrate
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
